import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Clock, Plus, Save, Upload } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import RichTextInput from "@/components/common/RichTextInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHotelUpdate } from "@/components/pages/hotels/hooks/useHotels";
import {
  GeneralInfoFormData,
  generalInfoSchema,
} from "@/components/pages/general-info/utils/generalInfoSchema";
import {
  TIME_OPTIONS,
  TIMEZONE_OPTIONS,
} from "@/components/pages/general-info/utils/generalInfoOptions";
import { Hotel } from "@/types/hotel";
import { Role } from "@/types/user";
import { UserProfile } from "@/types/user-profile";
import { countries } from "@/utils/countries";
import { GeneralInfoLogoUploader } from "@/components/pages/general-info/components/GeneralInfoLogoUploader";

interface GeneralInfoFormProps {
  hotel: Hotel;
  managers: UserProfile[];
}

const FieldError = ({
  message,
}: {
  message?: string;
}): React.JSX.Element | null =>
  message ? <p className="text-destructive mt-1.5 text-sm">{message}</p> : null;

const getCountryCode = (country?: string): string => {
  const normalizedCountry = country?.trim();

  if (!normalizedCountry) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(normalizedCountry)) {
    return normalizedCountry.toUpperCase();
  }

  return (
    countries.find(
      ({ name }) => name.toLowerCase() === normalizedCountry.toLowerCase(),
    )?.code ?? ""
  );
};

const formatManagerLabel = (manager: UserProfile): string => {
  const fullName = [manager.firstName, manager.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  return fullName && manager.email
    ? `${fullName} - ${manager.email}`
    : fullName || manager.email || manager.id;
};

const getDefaultValues = (hotel: Hotel): GeneralInfoFormData => ({
  name: hotel.name,
  logoId: hotel.logoId,
  address: {
    street: hotel.hotelAddress?.street ?? "",
    city: hotel.hotelAddress?.city ?? "",
    state: hotel.hotelAddress?.state ?? "",
    country: getCountryCode(hotel.hotelAddress?.country),
    postalCode: hotel.hotelAddress?.postalCode ?? "",
  },
  pms: hotel.pms,
  wifiNetwork: hotel.wifiNetwork ?? "",
  wifiPassword: hotel.wifiPassword ?? "",
  mapFileId: hotel.mapFileId ?? "",
  policyText: hotel.policyText ?? "",
  timezone: hotel.timezone ?? "",
  website: hotel.website ?? "",
  checkInTime: hotel.checkInTime ?? "11:00",
  checkOutTime: hotel.checkOutTime ?? "14:00",
  socialMediaInstagram: hotel.socialMediaInstagram ?? "",
  socialMediaFacebook: hotel.socialMediaFacebook ?? "",
  socialMediaOther: hotel.socialMediaOther ?? "",
  managerIds:
    hotel.employees
      ?.filter(
        (employee) =>
          employee.role === Role.HOTEL_ADMIN ||
          employee.role === Role.TENANT_ADMIN,
      )
      .map((employee) => employee.id) ?? [],
  chatSettings: hotel.chatSettings
    ? {
        isAiEnabled: hotel.chatSettings.isAiEnabled,
        isTranslationEnabled: hotel.chatSettings.isTranslationEnabled,
        defaultLanguage: hotel.chatSettings.defaultLanguage,
      }
    : undefined,
});

export const GeneralInfoForm = ({
  hotel,
  managers,
}: GeneralInfoFormProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { handleHotelSubmit, isLoading: isUpdating } = useHotelUpdate(hotel.id);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File>();
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>();
  const [selectedMapFile, setSelectedMapFile] = useState<File>();
  const [policyText, setPolicyText] = useState<string | undefined>(
    hotel.policyText,
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<GeneralInfoFormData>({
    resolver: zodResolver(generalInfoSchema),
    defaultValues: getDefaultValues(hotel),
  });

  useEffect(() => {
    reset(getDefaultValues(hotel));
    setPolicyText(hotel.policyText);
    setSelectedLogoFile(undefined);
    setSelectedMapFile(undefined);
    setLogoPreviewUrl(undefined);
  }, [hotel, reset]);

  useEffect(
    () => () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    },
    [logoPreviewUrl],
  );

  const selectedCountry = watch("address.country");
  const selectedTimezone = watch("timezone");
  const selectedCheckInTime = watch("checkInTime");
  const selectedCheckOutTime = watch("checkOutTime");
  const selectedManagers = watch("managerIds") ?? [];
  const assignedManager = managers.find((manager) =>
    selectedManagers.includes(manager.id),
  );
  const countryName =
    countries.find(({ code }) => code === selectedCountry)?.name ??
    hotel.hotelAddress?.country ??
    "";
  const preferredLanguage = hotel.chatSettings?.defaultLanguage
    ? hotel.chatSettings.defaultLanguage.toUpperCase()
    : t("hotels.generalInfo.notConfigured");
  const displayCurrency = hotel.paymentSettings?.currency
    ? hotel.paymentSettings.currency.toUpperCase()
    : t("hotels.generalInfo.notConfigured");
  const hasPolicyChanges = (policyText ?? "") !== (hotel.policyText ?? "");
  const hasFileChanges = Boolean(selectedLogoFile || selectedMapFile);
  const isSaving = isSubmitting || isUpdating;
  const canSave = isDirty || hasPolicyChanges || hasFileChanges;

  const handleLogoChange = (file: File) => {
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }

    setSelectedLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleMapFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setSelectedMapFile(file);
    }
  };

  const onSubmit = async (values: GeneralInfoFormData) => {
    try {
      // General Info does not own payment settings; keep currency managed by Billing Settings.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars
      const { paymentSettings: _, ...hotelValues } = values;
      // Empty social links must be sent as null, not "", so the backend clears
      // them instead of rejecting an empty string as an invalid URL.
      const normalizedValues = {
        ...hotelValues,
        socialMediaInstagram: hotelValues.socialMediaInstagram || null,
        socialMediaFacebook: hotelValues.socialMediaFacebook || null,
        socialMediaOther: hotelValues.socialMediaOther || null,
      };
      await handleHotelSubmit(
        { ...normalizedValues, policyText },
        selectedLogoFile,
        hotel.logoId,
        selectedMapFile,
        hotel.mapFileId,
      );
      reset({ ...hotelValues, policyText });
      setSelectedLogoFile(undefined);
      setSelectedMapFile(undefined);
      toast.success(t("hotels.form.hotelUpdatedSuccessfully"));
    } catch (error) {
      toast.error(t("hotels.form.failedToUpdateHotel"));
      console.error("Error updating hotel general information:", error);
    }
  };

  return (
    <main className="bg-background min-h-full p-4 sm:p-6 lg:p-10">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <header className="flex flex-col items-center gap-4 text-center">
          <GeneralInfoLogoUploader
            fileId={hotel.logoId}
            previewUrl={logoPreviewUrl}
            alt={t("hotels.generalInfo.logoAlt", { name: hotel.name })}
            changeLabel={t("hotels.generalInfo.changeLogo")}
            onChange={handleLogoChange}
          />
          <div className="flex max-w-xl flex-col gap-1">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {hotel.name}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("hotels.generalInfo.description")}
            </p>
          </div>
        </header>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
          noValidate
        >
          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardContent className="grid gap-5 pt-2 md:grid-cols-2">
              <Input
                label={`${t("hotels.generalInfo.hotelBrandName")} *`}
                value={hotel.name}
                readOnly
              />

              <div className="hidden md:block" aria-hidden="true" />

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.checkInTime")} *
                </label>
                <Select
                  value={selectedCheckInTime}
                  onValueChange={(value) =>
                    setValue("checkInTime", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <Clock className="size-4" />
                    <SelectValue
                      placeholder={t("hotels.generalInfo.selectTime")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.checkInTime?.message} />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.checkOutTime")} *
                </label>
                <Select
                  value={selectedCheckOutTime}
                  onValueChange={(value) =>
                    setValue("checkOutTime", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <Clock className="size-4" />
                    <SelectValue
                      placeholder={t("hotels.generalInfo.selectTime")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.checkOutTime?.message} />
              </div>

              <Input
                label={`${t("hotels.generalInfo.preferredLanguage")} *`}
                value={preferredLanguage}
                readOnly
              />
              <Input
                label={`${t("hotels.generalInfo.displayCurrency")} *`}
                value={displayCurrency}
                readOnly
              />
              <div>
                <Input
                  type="url"
                  label={`${t("hotels.generalInfo.website")} *`}
                  placeholder={t("hotels.generalInfo.websitePlaceholder")}
                  aria-invalid={Boolean(errors.website)}
                  {...register("website")}
                />
                <FieldError message={errors.website?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.addressInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              <Input
                label={`${t("hotels.form.country")} *`}
                value={countryName}
                readOnly
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-foreground text-sm font-medium">
                  {t("hotels.generalInfo.timezone")} *
                </label>
                <Select
                  value={selectedTimezone}
                  onValueChange={(value) =>
                    setValue("timezone", value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t("hotels.generalInfo.selectTimezone")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.timezone?.message} />
              </div>

              <Input
                label={t("hotels.form.state")}
                value={hotel.hotelAddress?.state ?? ""}
                readOnly
              />

              <Input
                label={`${t("hotels.form.city")} *`}
                value={hotel.hotelAddress?.city ?? ""}
                readOnly
              />

              <Input
                label={`${t("hotels.form.street")} *`}
                value={hotel.hotelAddress?.street ?? ""}
                readOnly
              />

              <Input
                label={t("hotels.form.postalCode")}
                value={hotel.hotelAddress?.postalCode ?? ""}
                readOnly
              />
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.wlanInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              <div>
                <Input
                  label={t("hotels.generalInfo.networkName")}
                  placeholder={t("hotels.form.enterWifiNetworkName")}
                  aria-invalid={Boolean(errors.wifiNetwork)}
                  {...register("wifiNetwork")}
                />
                <FieldError message={errors.wifiNetwork?.message} />
              </div>
              <div>
                <Input
                  type="password"
                  label={t("hotels.generalInfo.password")}
                  placeholder={t("hotels.form.enterWifiPassword")}
                  aria-invalid={Boolean(errors.wifiPassword)}
                  autoComplete="new-password"
                  {...register("wifiPassword")}
                />
                <FieldError message={errors.wifiPassword?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.hotelMap")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <label
                htmlFor="mapFileId"
                className="bg-background hover:bg-accent focus-within:ring-ring/50 flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed transition-colors focus-within:ring-3"
              >
                <Upload className="size-6" />
                <span className="text-muted-foreground text-sm">
                  {selectedMapFile?.name ??
                    (hotel.mapFileId
                      ? t("hotels.form.currentFileUploaded")
                      : t("hotels.generalInfo.uploadMap"))}
                </span>
                <input
                  id="mapFileId"
                  type="file"
                  accept=".pdf"
                  className="sr-only"
                  onChange={handleMapFileChange}
                />
              </label>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.contacts")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 lg:grid-cols-3">
              <Input
                label={t("hotels.generalInfo.assignedManager")}
                value={
                  assignedManager ? formatManagerLabel(assignedManager) : ""
                }
                readOnly
              />
              <Input
                label={t("hotels.generalInfo.phone")}
                value={assignedManager?.phone ?? ""}
                readOnly
              />
              <Input
                type="email"
                label={t("hotels.generalInfo.email")}
                value={assignedManager?.email ?? ""}
                readOnly
              />
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <CardTitle className="text-primary text-xl font-normal">
                {t("hotels.generalInfo.socialMedia")}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 lg:grid-cols-3">
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.instagram")}
                  placeholder={t("hotels.generalInfo.instagramPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaInstagram)}
                  {...register("socialMediaInstagram")}
                />
                <FieldError message={errors.socialMediaInstagram?.message} />
              </div>
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.facebook")}
                  placeholder={t("hotels.generalInfo.facebookPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaFacebook)}
                  {...register("socialMediaFacebook")}
                />
                <FieldError message={errors.socialMediaFacebook?.message} />
              </div>
              <div>
                <Input
                  type="url"
                  label={t("hotels.generalInfo.other")}
                  placeholder={t("hotels.generalInfo.otherSocialPlaceholder")}
                  aria-invalid={Boolean(errors.socialMediaOther)}
                  {...register("socialMediaOther")}
                />
                <FieldError message={errors.socialMediaOther?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#F3F4F6] shadow-none">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-primary text-xl font-normal">
                  {t("hotels.generalInfo.hotelPolicies")}
                </CardTitle>
                <Button
                  type="button"
                  variant="new_secondary"
                  size="lg"
                  onClick={() =>
                    setPolicyText((current) =>
                      current ? `${current}\n- ` : "- ",
                    )
                  }
                >
                  <Plus />
                  {t("hotels.generalInfo.addRule")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <RichTextInput
                value={policyText}
                dataColorMode="light"
                onChange={setPolicyText}
                containerClassName="flex flex-col gap-1.5"
              />
            </CardContent>
          </Card>

          <div className="flex flex-col items-end justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-muted-foreground text-sm">
              {canSave
                ? t("hotels.generalInfo.unsavedChanges")
                : t("hotels.generalInfo.allChangesSaved")}
            </p>
            <Button
              type="submit"
              variant="new_default"
              size="lg"
              disabled={!canSave || isSaving}
            >
              <Save />
              {isSaving
                ? t("hotels.form.saving")
                : t("hotels.generalInfo.saveChanges")}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
};
